import { ThemeEvents } from '@theme/events';

class VowPackOptions extends HTMLElement {
  async connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    await customElements.whenDefined('quantity-selector-component');
    if (!this.isConnected || signal.aborted) return;
    this.form = this.closest('form');
    this.quantitySelector = this.form?.querySelector('quantity-selector-component');
    this.quantityInput = this.quantitySelector?.querySelector('input[name="quantity"]');
    if (!this.form || !this.quantityInput) return;
    this.nativeMinimum = Number(this.quantityInput.min) || 1;
    this.nativeStep = Number(this.quantityInput.step) || 1;
    this.nativeMaximum = this.dataset.stockLimit === '' ? null : Number(this.dataset.stockLimit);
    this.quantityInput.setAttribute('aria-label', this.dataset.quantityLabel);
    this.money = new Intl.NumberFormat(this.dataset.language === 'en' ? 'en-HK' : 'zh-HK', {
      style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    this.number = new Intl.NumberFormat(this.dataset.language === 'en' ? 'en-HK' : 'zh-HK', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) this.selectPack(Number(event.target.value));
    }, { signal });
    this.form.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.updateTotal(), { signal });
    this.quantityInput.addEventListener('input', () => this.updateTotal(), { signal });
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.updateAvailability();
        this.constrainQuantity(false);
        this.updateTotal();
      });
    }, { signal });
    this.updateAvailability();
    this.packSize = Number(this.querySelector('input:checked')?.value) || 1;
    this.constrainQuantity(false);
    this.updateTotal();
    this.hidden = false;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  remainingUnits() {
    if (this.nativeMaximum === null) return Infinity;
    return Math.max(0, this.nativeMaximum - (Number(this.quantityInput.dataset.cartQuantity) || 0));
  }

  updateAvailability() {
    const remaining = this.remainingUnits();
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      const size = Number(radio.value);
      radio.disabled = this.dataset.available !== 'true' || size > remaining || size < this.nativeMinimum ||
        (size - this.nativeMinimum) % this.nativeStep !== 0 || size % this.nativeStep !== 0;
    }
  }

  selectPack(size) {
    this.packSize = size;
    this.constrainQuantity(true);
    this.quantitySelector.onQuantityChange();
    this.updateTotal();
  }

  constrainQuantity(reset) {
    const size = this.packSize || 1;
    const cartQuantity = Number(this.quantityInput.dataset.cartQuantity) || 0;
    const maximum = this.nativeMaximum === null ? null : cartQuantity + Math.floor(this.remainingUnits() / size) * size;
    this.quantityInput.min = String(size);
    this.quantityInput.step = String(size);
    if (maximum === null) this.quantityInput.removeAttribute('max');
    else this.quantityInput.max = String(maximum);
    if (reset) this.quantitySelector.setValue(String(size));
    else {
      const quantity = Number(this.quantityInput.value) || size;
      const aligned = Math.max(size, Math.floor(quantity / size) * size);
      this.quantitySelector.setValue(String(maximum === null ? aligned : Math.max(size, Math.min(aligned, maximum - cartQuantity))));
    }
    this.quantitySelector.updateButtonStates();
  }

  updateTotal() {
    const quantity = Math.max(0, Number(this.quantityInput.value) || 0);
    const original = Number(this.dataset.unitPrice) * quantity;
    const discounted = quantity >= Number(this.dataset.discountThreshold);
    const total = (discounted ? Number(this.dataset.discountPrice) : Number(this.dataset.unitPrice)) * quantity;
    this.querySelector('[data-vow-pack-total]').textContent = this.formatMoney(total);
    const originalNode = this.querySelector('[data-vow-pack-original]');
    originalNode.hidden = !discounted;
    originalNode.textContent = this.formatMoney(original);
  }

  formatMoney(amount) {
    return this.dataset.currency === 'HKD' ? `HK$${this.number.format(amount / 100)}` : this.money.format(amount / 100);
  }
}

if (!customElements.get('vow-pack-options')) customElements.define('vow-pack-options', VowPackOptions);
